/**
 * API client for vchat2 GetDeepLinkPublic endpoint.
 * Used to resolve deep links (usernames, social IDs, channel invite links)
 * for the vama.me website preview pages.
 */

// Default to production channel API
const VCHAT2_API_URL = process.env.REACT_APP_VCHAT2_API_URL || 'https://channel.api.v2.vama.com';

/**
 * Calls the GetDeepLinkPublic endpoint to resolve a deep link.
 * @param {string} inviteSuffix - The username, social ID, or channel invite link
 * @returns {Promise<{account?: object, channel?: object}>} - The resolved account or channel
 */
export async function getDeepLinkPublic(inviteSuffix) {
  const url = `${VCHAT2_API_URL}/vchat.channel.v1.Service/GetDeepLinkPublic`;
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      invite_suffix: inviteSuffix,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(errorData.message || `API error: ${response.status}`);
    error.code = errorData.code || 'UNKNOWN';
    error.status = response.status;
    throw error;
  }

  return response.json();
}

/**
 * Transforms the GetDeepLinkPublic account response to the legacy format
 * expected by the TempRedirect component.
 * @param {object} accountData - The account data from GetDeepLinkPublic
 * @returns {object} - Legacy format account data
 */
export function transformAccountResponse(accountData) {
  // Parse display_name into first_name and last_name
  const nameParts = (accountData.display_name || '').split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  return {
    account: {
      id: accountData.id,
      first_name: firstName,
      last_name: lastName,
      avatar: accountData.high_res_avatar || accountData.avatar || '',
      textContent: accountData.bio || '',
      username: accountData.username || '',
    },
  };
}

/**
 * Transforms the GetDeepLinkPublic channel response to the legacy format
 * expected by the TempRedirect component.
 * @param {object} channelData - The channel data from GetDeepLinkPublic
 * @returns {object} - Legacy format channel data
 */
export function transformChannelResponse(channelData) {
  return {
    public_link_channel: {
      id: channelData.id,
      avatar: channelData.high_res_avatar || channelData.avatar || '',
      member_count: channelData.member_count || 0,
      metadata: {
        name: channelData.name || '',
        public: {
          description: channelData.description || '',
        },
      },
      // Note: hosts data is not available from the public endpoint
      // The UI will need to handle missing hosts gracefully
      hosts: [],
      admins: [],
    },
  };
}

/**
 * Transforms the GetDeepLinkPublic channel response for private links.
 * @param {object} channelData - The channel data from GetDeepLinkPublic
 * @returns {object} - Legacy format for private channel data
 */
export function transformPrivateChannelResponse(channelData) {
  return {
    private_link_channel: {
      id: channelData.id,
      metadata: {
        name: channelData.name || '',
      },
    },
  };
}
